import { Controller } from "@hotwired/stimulus"
import { currentLevel, LEVEL_OFFLINE, LEVEL_DEGRADED } from "../field_network_health"
import { missionDownloadCounts } from "../field_mission_download"

// "N of M missions downloaded" plus a plain notice when the signal is weak
// (card #4117), so a mission that is not on the phone is expected and
// explained instead of a mystery. Shown on the Sync and Today pages.
//
// The markup holds every piece hidden; this controller only writes text and
// toggles `hidden`. Re-renders when anything that changes the answer happens:
// a sync finishing, a mission downloading, the connection changing.
//
//   data-controller="field-mission-counts"
//   targets: card, count, notice

const EVENTS = [
  "field:sync-complete", "field:prefetch-complete", "field:mission-downloaded",
  "field:mission-counts-changed", "field:online", "field:offline", "field:health-changed"
]

export default class extends Controller {
  static targets = ["count", "notice"]

  connect() {
    this._handler = () => this.render()
    EVENTS.forEach(name => window.addEventListener(name, this._handler))
    this.render()
  }

  disconnect() {
    EVENTS.forEach(name => window.removeEventListener(name, this._handler))
  }

  async render() {
    let counts
    try {
      counts = await missionDownloadCounts()
    } catch {
      return
    }
    if (!this.element.isConnected) return

    const { downloaded, assigned, queued } = counts
    const level = currentLevel()
    const never = !localStorage.getItem("field:last-prefetch") && assigned === 0

    // Nothing synced yet: the sync page already says "Not yet synced".
    this.element.hidden = never
    if (never) return

    const noun = assigned === 1 ? "mission" : "missions"
    this.countTarget.textContent = `${downloaded} of ${assigned} ${noun} downloaded`

    const notice = this._noticeFor({ downloaded, assigned, queued, level })
    this.noticeTarget.textContent = notice
    this.noticeTarget.hidden = notice === ""
  }

  _noticeFor({ downloaded, assigned, queued, level }) {
    const missing = assigned - downloaded
    if (level === LEVEL_OFFLINE) {
      return "You're offline. Any mission that isn't downloaded yet will download when you have a connection."
    }
    if (level === LEVEL_DEGRADED) {
      return "Your signal is weak. Some missions may not be on your phone yet. We'll keep trying, and you can open any mission to download it."
    }
    if (missing > 0) {
      const which = missing === 1 ? "1 mission isn't" : `${missing} missions aren't`
      const tail = queued > 0 ? ` ${queued} ${queued === 1 ? "is" : "are"} waiting to download.` : ""
      return `${which} on your phone yet. Tap Sync now, or open a mission to download it.${tail}`
    }
    return ""
  }
}
